import type { ReactNode } from 'react';
import type { AccessState as ControlAccess } from '@viniciomendesr/rayrovisk-design-system';
import { grantsContent, type AccessState } from '../api/client';
import type { Me, ProductId } from '../api/types';

/**
 * The navigation, as data.
 *
 * This is the only file in the product where two vocabularies meet, and they
 * collide by name:
 *
 *  - `AccessState` from `../api/client` — `granted | not_owned | revoked |
 *    in_review`. What she bought.
 *  - `AccessState` from the design system, imported here as `ControlAccess` —
 *    `editable | readonly | disabled | unavailable | permission-required`. What
 *    a control lets her do.
 *
 * They are unrelated types with one name, and everything below translates the
 * first into the second. Nothing else in the app should have to.
 *
 * Everything the shell draws — the padlock, the greyed label, the area badge,
 * the counter — is derived from this table plus `/v1/me`. Nothing is derived
 * from the URL, which is what the shell used to do and what made a navigation
 * entry unable to say whether she owned it.
 *
 * **It is appearance, never authority.** Every route keeps its server-side
 * gate. Drawing a padlock is a courtesy; the 403 is the boundary.
 */

/** `requires` mirrors each server route so the padlock never overstates access. */

/** `Montador de prompt` is not built. A separate sentence because it is a separate fact. */
export const WITHHELD_NOT_BUILT = 'O montador de prompt ainda está sendo construído.';

/**
 * A feature that is announced and not built. Distinct from `WITHHELD_NOT_BUILT`
 * above in one way that matters: that one makes an entry inert, this one leaves
 * it clickable, because the screen behind it is the whole point — it says the
 * function is not ready and asks her what she would want in it.
 */
export const IN_DEVELOPMENT_REASON = 'Ainda estamos construindo. Toque para dizer o que você quer aqui.';

const NOT_OWNED_REASON = 'Este produto não está no seu acesso.';
const REVOKED_REASON = 'Este acesso foi encerrado pelo reembolso.';

/**
 * One row of the manifest — deliberately **not** called `NavEntry`, which is the
 * design system's component for rendering one. The shell imports both, and two
 * unrelated `NavEntry`s in one file would need an alias at every call site and
 * produce a type error that reads like a bug in the package. Data is an item;
 * the thing that draws it is an entry.
 */
export interface NavItem {
  id: string;
  label: string;
  to: string;
  /** Decorative; the label carries the accessible name. */
  icon?: ReactNode;
  /**
   * Any one of these opens the entry. A list rather than a single product
   * because some entries are shared by more than one product. Empty means open
   * to anyone signed in, which is how account-owned surfaces such as `Meu
   * perfil` stay independent from the product ladder.
   */
  requires: readonly ProductId[];
  /**
   * Set when the entry is owned but deliberately withheld. Second in
   * precedence, never first: see `entryAccess`.
   */
  withheldReason?: string;
  /**
   * Announced, not built, and still clickable. The padlock is drawn and the
   * route behind it explains — which is the shell's existing rule for a locked
   * entry, not a second one.
   */
  inDevelopment?: true;
  /** Draws a number in the trailing slot instead of a padlock. */
  counter?: 'pinned';
  /** Built or not, reachable by URL, kept off the list — same rule as an area's. */
  hidden?: boolean;
}

export interface NavArea {
  id: string;
  label: string;
  /** Products whose states the area badge summarises. */
  summarises: readonly ProductId[];
  /** The badge shows a credit balance rather than a word. */
  spendsCredits?: boolean;
  /** Built and reachable by URL, kept off the list (D5). */
  hidden?: boolean;
  /**
   * A grouping rather than something she buys. It gets a section in the list
   * and nothing else: no card under `Seus acessos`, no icon on the rail. Both
   * of those answer "what did I pay for", and an answer that is not a product
   * makes the question unreadable.
   */
  grouping?: boolean;
  entries: readonly NavItem[];
}

export const NAVIGATION: readonly NavArea[] = [
  {
    id: 'prompts',
    label: 'Gerador de Prompts',
    summarises: ['prompt-pack-starter', 'prompt-pack', 'prompt-generator'],
    entries: [
      // Every step of the ladder opens it, the R$ 297 one included from
      // 2026-08-06. What differs is how much of the library answers — the
      // starter reads her twenty, everyone above her reads the file — and that
      // is the server's call, not the padlock's.
      {
        id: 'biblioteca',
        label: 'Biblioteca de Prompts',
        to: '/biblioteca',
        requires: ['prompt-pack-starter', 'prompt-pack', 'prompt-generator']
      },
      {
        // `Gerar prompt` since 2026-08-06, and it took the name back from the
        // entry below. The id and the URL keep saying `montar` on purpose: one
        // is internal and the other is a bookmark a lifetime buyer may hold.
        id: 'montar',
        label: 'Gerar prompt',
        to: '/gerador/montar',
        requires: ['prompt-generator']
      },
      {
        // Renamed out of the way twice on 2026-08-06: it lost `Gerar prompt` to
        // the entry above, was briefly `Refinar com IA`, and the owner settled
        // on `Montador de prompt`.
        //
        // **The name and the screen disagree, and that is known.** What is built
        // behind this route is an AI rewriter — it refines a prompt that already
        // exists — while the thing that actually assembles one is the entry
        // above. Nobody reading this nav can tell that from the labels. It is a
        // label on an unbuilt feature, so it costs nothing today; the day this
        // ships, either the name or the screen has to give.
        id: 'gerar-ia',
        label: 'Montador de prompt',
        to: '/gerador/ia',
        requires: ['prompt-generator'],
        withheldReason: WITHHELD_NOT_BUILT,
        // Off the list until the name and the screen stop disagreeing. The
        // reason above still applies and the route still answers; what changed
        // is that a padlock on a thing nobody can unlock is not information.
        hidden: true
      },
    ]
  },
  {
    id: 'scripts',
    label: 'Gerador de Roteiros',
    summarises: ['script-generator'],
    spendsCredits: true,
    entries: [
      { id: 'roteiros', label: 'Gerar roteiro', to: '/roteiros', requires: [] }
    ]
  },
  {
    // A grouping, not a product: what she keeps and who she is, rather than
    // what she bought. `Fixados` sits here with `Meu perfil` because both are
    // hers rather than a generator's.
    //
    // Pinning is a function of the account, not of a product (see
    // `routes/pins.ts`, `PACK_PRODUCTS`): `requires` below lists every product
    // that can produce something to pin, so the starter opens the entry too —
    // she can pin from the library — while the other two kinds stay gated
    // where they are made, and a starter buyer simply never has one of those.
    id: 'meu-perfil',
    label: 'Meu perfil',
    summarises: [],
    grouping: true,
    entries: [
      {
        id: 'fixados',
        label: 'Fixados',
        to: '/gerador/fixados',
        requires: ['prompt-generator', 'prompt-pack', 'prompt-pack-starter'],
        counter: 'pinned'
      },
      // The profile belongs to the account, not to a product. Authentication is
      // the gate; the products only decide what can consume the saved profile.
      {
        id: 'perfil',
        label: 'Meu perfil',
        to: '/gerador/perfil',
        requires: []
      },
    ]
  },
  {
    // What is announced before it is finished. Its own section so the promise
    // is scoped: everything above works, everything here is either still moving
    // or not built at all, and one label carries that instead of four padlocks
    // that look like a price.
    id: 'em-teste',
    label: 'Funções em teste',
    summarises: [],
    grouping: true,
    // Built or not, reachable by URL, kept off the list — same rule as
    // `analysis` below. Asked for by the account owner rather than by any
    // entry inside it changing state.
    hidden: true,
    entries: [
      // Same reason as the profile above: the numbers are her account's, not a
      // product's, and nothing here reads a purchase.
      //
      // Named for what she brings rather than for what the screen computes. The
      // entry read `Meus números` from 2026-08-18 until the owner read it in the
      // menu the same evening: from here the click is "as minhas redes", and the
      // numbers are what she finds after it. The screen keeps its own title and
      // the id keeps saying `midia-kit`, which is what the URL and the tests
      // hold on to.
      //
      // It moved here from `Meu perfil` on 2026-08-19, and it is the only entry
      // in this section that opens: it works, and it reads a relay in somebody's
      // house while its authorization is open to a Meta tester list, which is
      // what "em teste" is describing.
      {
        id: 'midia-kit',
        label: 'Minhas redes',
        to: '/midia-kit',
        requires: []
      },
      // The three below are announced and unbuilt. They carry no `requires`,
      // because nothing about them is a purchase — the padlock here says "not
      // finished", never "not bought", and the screen behind each says so in
      // those words before asking her what belongs in it.
      { id: 'ideias', label: 'Minhas ideias', to: '/ideias', requires: [], inDevelopment: true },
      { id: 'memorias', label: 'Minhas memórias', to: '/memorias', requires: [], inDevelopment: true },
      { id: 'agentes', label: 'Meus agentes', to: '/agentes', requires: [], inDevelopment: true }
    ]
  },
  {
    id: 'analysis',
    label: 'Análise de Instagram',
    summarises: [],
    hidden: true,
    entries: [{ id: 'analise', label: 'Tenho interesse', to: '/analise', requires: [] }]
  }
];

export function visibleEntries(area: NavArea): readonly NavItem[] {
  return area.entries.filter((entry) => !entry.hidden);
}

export function visibleAreas(): readonly NavArea[] {
  return NAVIGATION.filter((area) => !area.hidden);
}

/** The areas that answer "what did I pay for": the access cards and the rail. */
export function purchasableAreas(): readonly NavArea[] {
  return visibleAreas().filter((area) => !area.grouping);
}

/**
 * The best state across a set of products, by the same precedence
 * `accessStateFor` applies across rows on the server: a live right outranks one
 * under review, and a review outranks a revocation somewhere else. Someone with
 * a refunded pack and a live generator holds the area open.
 */
function bestState(products: readonly ProductId[], me: Me): AccessState {
  const states = products.map((product) => me.products[product]);
  if (states.includes('granted')) return 'granted';
  if (states.includes('in_review')) return 'in_review';
  if (states.includes('revoked')) return 'revoked';
  return 'not_owned';
}

export interface EntryAccess {
  access: ControlAccess;
  /** Why it is closed, in her language. Undefined when it is open. */
  reason?: string;
}

/**
 * **Ownership before availability, and the order is the point.**
 *
 * `Montador de prompt` is both unbought (by a pack buyer) and unbuilt (by anyone).
 * Those are two sentences and the right one depends on who is looking:
 *
 *  - A pack buyer gets `permission-required`, stays able to click, and lands on
 *    the sale. From where she stands the honest answer is "buy the generator";
 *    telling her a feature she does not own is not ready yet answers a question
 *    she did not ask.
 *  - A generator owner gets `unavailable` and an inert entry. She bought it, so
 *    the only true thing left to say is that this part is not ready.
 *
 * Reversing the two would show a pack buyer "ainda não disponível" for
 * something she was never sold, which reads as a broken product rather than as
 * a price.
 */
export function entryAccess(entry: NavItem, me: Me): EntryAccess {
  // Before the open-to-everyone shortcut: these entries require nothing, so
  // that branch would return them editable and the padlock would never draw.
  //
  // `permission-required` and not `unavailable`, which is the opposite of
  // `Montador de prompt` above: that one is inert because there is nothing to
  // land on, and this one has to stay clickable because the screen behind it
  // is the entire feature today.
  if (entry.inDevelopment) {
    return { access: 'permission-required', reason: IN_DEVELOPMENT_REASON };
  }

  if (entry.requires.length === 0) {
    return { access: 'editable' };
  }

  const state = bestState(entry.requires, me);
  if (!grantsContent(state)) {
    return {
      access: 'permission-required',
      // A refund is not "you never bought this". The design system has one
      // value for both, so the sentence is where the difference survives.
      reason: state === 'revoked' ? REVOKED_REASON : NOT_OWNED_REASON
    };
  }

  if (entry.withheldReason) {
    return { access: 'unavailable', reason: entry.withheldReason };
  }

  return { access: 'editable' };
}

/** Zero draws nothing: a `0` beside a navigation entry is noise. */
export function entryCount(entry: NavItem, me: Me): number | undefined {
  if (entry.counter !== 'pinned') {
    return undefined;
  }
  return me.pinnedCount > 0 ? me.pinnedCount : undefined;
}

export interface AreaBadge {
  tone: 'success' | 'accent' | 'warning' | 'error' | 'neutral';
  variant: 'subtle' | 'outline';
  text: string;
}

/**
 * What the access card says. The product decides the words; the design system
 * only places them.
 *
 * "grátis" rather than "créditos" is derived from not owning
 * `prompt-generator`, which is exactly the funnel semantics: the credits are
 * free until the moment she buys the product that ships a hundred of them.
 */
export function areaBadge(area: NavArea, me: Me): AreaBadge {
  const state = bestState(area.summarises, me);

  if (area.spendsCredits) {
    const paid = me.products['prompt-generator'] === 'granted';
    return {
      tone: 'accent',
      variant: 'subtle',
      text: `${me.scriptCredits.balance} ${paid ? 'créditos' : 'grátis'}`
    };
  }

  if (area.summarises.length === 0) {
    return { tone: 'neutral', variant: 'outline', text: 'em breve' };
  }

  if (state === 'granted') return { tone: 'success', variant: 'subtle', text: 'ativo' };
  if (state === 'in_review') return { tone: 'warning', variant: 'subtle', text: 'em análise' };
  if (state === 'revoked') return { tone: 'error', variant: 'subtle', text: 'encerrado' };
  return { tone: 'neutral', variant: 'outline', text: 'não incluído' };
}

/**
 * Where the area card points: the first entry she can actually open.
 *
 * Sending a generator buyer to the library she never bought would make her own
 * product's card answer 403. The fallback is the first entry, so an area she
 * holds nothing in still lands somewhere that explains itself.
 */
export function areaLanding(area: NavArea, me: Me): string {
  const open = area.entries.find((entry) => entryAccess(entry, me).access === 'editable');
  return (open ?? area.entries[0]!).to;
}
